import React, { useMemo } from 'react';
import { SearchableSelect } from '../../utils/select-with-search';
import { DepartmentHoliday } from './types';

export const formatHolidayDate = (value: string) => {
  // holiday_date is a plain YYYY-MM-DD day; read it as written, not as UTC.
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  if (!match) return value || '';
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

export const holidayLabel = (holiday: DepartmentHoliday) =>
  `${holiday.holiday_name} (${formatHolidayDate(holiday.holiday_date)})`;

interface HolidayMultiSelectProps {
  holidays: DepartmentHoliday[];
  /** Holidays already linked to the department, so they show even if missing from `holidays`. */
  linked?: DepartmentHoliday[];
  value: number[];
  onChange: (ids: number[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

// Holidays of a department, with "Select all visible". Same links the Holiday
// Calendar edits from the holiday side.
export const HolidayMultiSelect: React.FC<HolidayMultiSelectProps> = ({
  holidays,
  linked = [],
  value,
  onChange,
  loading = false,
  disabled = false
}) => {
  const options = useMemo(() => {
    const byId = new Map<number, DepartmentHoliday>();
    linked.forEach((holiday) => byId.set(holiday.id, holiday));
    holidays.forEach((holiday) => byId.set(holiday.id, holiday));
    return Array.from(byId.values())
      .sort((a, b) => String(a.holiday_date).localeCompare(String(b.holiday_date)))
      .map((holiday) => ({
        value: holiday.id,
        label:
          holiday.department_ids && holiday.department_ids.length === 0
            ? `${holidayLabel(holiday)} · all departments`
            : holidayLabel(holiday)
      }));
  }, [holidays, linked]);

  return (
    <div>
      <SearchableSelect
        value={options.filter((option) => value.includes(Number(option.value)))}
        onChange={(selected: { value: number | string }[] = []) =>
          onChange(selected.map((option) => Number(option.value)))
        }
        options={options}
        placeholder="Search and select holidays"
        disabled={disabled}
        isLoading={loading}
        isMulti
        showSelectAllVisible
        // Rendered above the page so the offcanvas doesn't clip the menu.
        portalMenu
        emptyMessage="No holidays found"
      />
      <p className="mt-1 text-xs text-gray-500">
        A holiday with no department applies to all departments. Picking it here limits it to the departments it is
        picked for.
      </p>
    </div>
  );
};
